import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";

import type { IdentifiedWindowEntry } from "contexts/windows";

import { DOCK_MINIMIZED_HEIGHT, DOCK_PANEL_GAP } from "./constants";

export interface DockSlotLayout {
    id: string;
    height: number;
    entry: IdentifiedWindowEntry;
}

export interface UseDockLayoutResult {
    setZoneRef: (el: HTMLDivElement | null) => void;
    slots: DockSlotLayout[];
    /** Dock zone bounding rect in viewport coords; null until measured. */
    zoneRect: DOMRect | null;
}

/** Per-slot heights: minimized = const, pinned = stored, rest split leftover space. Re-runs on container resize. */
export function useDockLayout(dockedWindows: IdentifiedWindowEntry[]): UseDockLayoutResult {
    const zoneRef = useRef<HTMLDivElement | null>(null);
    const [zoneRect, setZoneRect] = useState<DOMRect | null>(null);

    // Memoized; keep the last rect on detach to avoid a Maximum-Update-Depth
    // loop when the dock toggles visibility synchronously.
    const setZoneRef = useCallback((el: HTMLDivElement | null) => {
        zoneRef.current = el;
        if (el) {
            setZoneRect(el.getBoundingClientRect());
        }
    }, []);

    useLayoutEffect(() => {
        const el = zoneRef.current;
        if (!el || typeof ResizeObserver === "undefined") {
            return;
        }
        const observer = new ResizeObserver(() => {
            setZoneRect(el.getBoundingClientRect());
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const zoneH = zoneRect?.height ?? 0;

    const slots = useMemo<DockSlotLayout[]>(() => {
        const n = dockedWindows.length;
        if (n === 0) {
            return [];
        }

        const fixedTotal = dockedWindows.reduce(
            (sum, w) => sum + (w.state.minimized ? DOCK_MINIMIZED_HEIGHT : (w.state.dockedHeight ?? 0)),
            0,
        );
        const flexCount = dockedWindows.filter((w) => !w.state.minimized && w.state.dockedHeight == null).length;
        const gapTotal = Math.max(0, n - 1) * DOCK_PANEL_GAP;
        const flexH = flexCount > 0 ? Math.max(0, (zoneH - fixedTotal - gapTotal) / flexCount) : 0;

        return dockedWindows.map((entry) => {
            const height = entry.state.minimized ? DOCK_MINIMIZED_HEIGHT : (entry.state.dockedHeight ?? flexH);
            return { id: entry.id, height, entry };
        });
    }, [dockedWindows, zoneH]);

    return { setZoneRef, slots, zoneRect };
}
